import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "../RootStore";

export interface MediaDevice {
  deviceId: string;
  groupId: string;
  kind: MediaDeviceKind;
  label: string;
}

export class MediaStore {
  rootStore: RootStore;
  devices: MediaDevice[] = [];
  currentStream: MediaStream | null = null;
  isRecording = false;
  isCameraActive = false;
  isMicrophoneActive = false;
  cameraPermission: PermissionState = "prompt";
  microphonePermission: PermissionState = "prompt";
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      currentStream: false,
      // mediaRecorder: false,
      // recordedChunks: false,
    });
    this.rootStore = rootStore;

    this.checkPermissions();
    this.loadDevices();
  }

  async checkPermissions() {
    try {
      const camera = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      const microphone = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      runInAction(() => {
        this.cameraPermission = camera.state;
        this.microphonePermission = microphone.state;
      });

      camera.addEventListener("change", () => {
        runInAction(() => {
          this.cameraPermission = camera.state;
        });
      });

      microphone.addEventListener("change", () => {
        runInAction(() => {
          this.microphonePermission = microphone.state;
        });
      });
    } catch (error) {
      console.error("Failed to check media permissions:", error);
    }
  }

  async loadDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      runInAction(() => {
        this.devices = devices.map((device) => ({
          deviceId: device.deviceId,
          groupId: device.groupId,
          kind: device.kind,
          label:
            device.label || `${device.kind} ${device.deviceId.slice(0, 5)}`,
        }));
      });
    } catch (error) {
      console.error("Failed to enumerate devices:", error);
    }
  }

  async requestCameraPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());

      runInAction(() => {
        this.cameraPermission = "granted";
      });

      await this.loadDevices();
    } catch (error) {
      runInAction(() => {
        this.cameraPermission = "denied";
      });
      throw error;
    }
  }

  async requestMicrophonePermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());

      runInAction(() => {
        this.microphonePermission = "granted";
      });

      await this.loadDevices();
    } catch (error) {
      runInAction(() => {
        this.microphonePermission = "denied";
      });
      throw error;
    }
  }

  async startCamera(constraints?: MediaStreamConstraints) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        constraints || { video: true, audio: false }
      );

      runInAction(() => {
        this.currentStream = stream;
        this.isCameraActive = true;
      });

      return stream;
    } catch (error) {
      console.error("Failed to start camera:", error);
      throw error;
    }
  }

  async startMicrophone(constraints?: MediaStreamConstraints) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        constraints || { audio: true, video: false }
      );

      runInAction(() => {
        this.currentStream = stream;
        this.isMicrophoneActive = true;
      });

      return stream;
    } catch (error) {
      console.error("Failed to start microphone:", error);
      throw error;
    }
  }

  async startScreenCapture() {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      runInAction(() => {
        this.currentStream = stream;
      });

      return stream;
    } catch (error) {
      console.error("Failed to start screen capture:", error);
      throw error;
    }
  }

  startRecording(mimeType = "video/webm") {
    if (!this.currentStream) {
      throw new Error("No media stream available");
    }

    this.recordedChunks = [];
    this.mediaRecorder = new MediaRecorder(this.currentStream, {
      mimeType: MediaRecorder.isTypeSupported(mimeType)
        ? mimeType
        : "video/webm",
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.mediaRecorder.start();

    runInAction(() => {
      this.isRecording = true;
    });
  }

  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error("No recording in progress"));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, {
          type: this.mediaRecorder!.mimeType,
        });

        runInAction(() => {
          this.isRecording = false;
        });

        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }

  stopMedia() {
    if (this.currentStream) {
      this.currentStream.getTracks().forEach((track) => track.stop());

      runInAction(() => {
        this.currentStream = null;
        this.isCameraActive = false;
        this.isMicrophoneActive = false;
      });
    }
  }

  async takePhoto(videoElement: HTMLVideoElement): Promise<Blob> {
    const canvas = document.createElement("canvas");
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get canvas context");
    }

    context.drawImage(videoElement, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to capture photo"));
        }
      }, "image/jpeg");
    });
  }

  get cameras() {
    return this.devices.filter((device) => device.kind === "videoinput");
  }

  get microphones() {
    return this.devices.filter((device) => device.kind === "audioinput");
  }

  get speakers() {
    return this.devices.filter((device) => device.kind === "audiooutput");
  }

  dispose() {
    this.stopMedia();
  }
}
